import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import { ThemeContext } from "../App";
import { getSimilarSongs, getSimilarSongsPercent } from "../shared/utils/songs.utils";
import { AccessTokenDto, AudioFeature, emptyFeature, getAllLikedSongs, getLikedSongs, getLikedSongsTotalCount, getManyTracksAudioFeatures, ResponseStatus, Track } from "../spotify/spotify";
import GroupBuilder from "./GroupBuilder";
import PlaylistCreator from "./PlaylistCreator";
import SongSelector from "./SongSelector";

interface SongSelectorProps {
    collectSongs: (numSongs: number) => void;
    collectAllSongs: () => void;
    totalLikedCount: number;
}

const SongCollector: React.FC<SongSelectorProps> = ({ collectSongs, collectAllSongs, totalLikedCount }) => {
    const { theme } = useContext(ThemeContext);
    const [ numSongsInput, setNumSongs ] = useState<number | null>(50);

    return (
        <>
            <p className='text-xl my-3'>Organize Your Liked Songs</p>
            <p className="my-2">Number of songs to collect: </p>
            <div className="row align-center mx-5 mx-bw-5 my-2">
                {/*<input type="text" value={numSongsInput || ""} placeholder="50" onChange={(e) => setNumSongs(e.target.value)} className="border rounded-lg text-gray-700 px-2" />*/}
                <input type="range" value={numSongsInput || 0} min={0} max={totalLikedCount} step={10} onChange={(e) => setNumSongs(parseInt(e.target.value))} className={`border border-${theme === "dark" ? 'white' : 'gray-700'} rounded-lg bg-transparent`} />
                <input type="number" value={numSongsInput || 0} min={0} max={totalLikedCount} step={10} onChange={(e) => setNumSongs(parseInt(e.target.value))} className={`border border-${theme === "dark" ? 'white' : 'gray-700'} rounded-lg bg-transparent px-2`} />
            </div>
            <button className="my-3" onClick={() => collectSongs(numSongsInput || 0)} disabled={numSongsInput === null}>Collect Songs</button>
            <button className="my-3" onClick={collectAllSongs}>Collect All Liked Songs ({totalLikedCount})</button>
        </>
    );
};

const SongOrganizer: React.FC = () => {
    const [ token, setToken ] = useState<AccessTokenDto | null>(null);
    const [ searchParams, _setSearchParams ] = useSearchParams();
    const [ status, setStatus ] = useState<"loaded" | "loading" | "success" | "error">("loading");
    
    const [ likedSongs, setLikedSongs ] = useState<Track[]>([]);
    const [ totalLikedCount, setTotalLikedCount ] = useState(0);
    const [ similarSongs, setSimilarSongs ] = useState<Track[]>([]);
    const [ targetSong, setTargetSong ] = useState<Track | null>(null);
    const [ groupSongs, setGroupSongs ] = useState<Track[]>([]);

    const collectSongs = async (numSongs: number) => {
        //if (numSongsInput === null || isNaN(parseInt(numSongsInput)) || token === null) {
        if (token === null) {
            setStatus("error");
            return;
        }
        setStatus("loading");
        let currOffset = 0;
        let currTotal = 0;
        let collectedSongs: Track[] = [];
        while (currTotal < numSongs) {
            const songs = await getLikedSongs(token.accessToken, currOffset * 50, Math.min(50, (numSongs - currTotal)));
            if (songs === ResponseStatus.UnknownError || songs === ResponseStatus.Unauthorized) {
                setStatus("error");
                return;
            }
            collectedSongs.push(...songs.items);
            currTotal += Math.min(50, (numSongs - currTotal)); //50;
            currOffset++;
        }
        setLikedSongs(collectedSongs);
        setStatus("loaded");
    }

    const collectAllSongs = async () => {
        if (token === null) {
            setStatus("error");
            return;
        }
        setStatus("loading");
        const songs = await getAllLikedSongs(token.accessToken);
        if (songs === ResponseStatus.UnknownError || songs === ResponseStatus.Unauthorized) {
            setStatus("error");
            return;
        }
        setLikedSongs(songs);
        setStatus("loaded");
    }

    const findSimilarSongs = async (targetSong: Track, minSimilarity: number) => {
        console.log(minSimilarity);
        const targetIdx = likedSongs.indexOf(targetSong);
        if (targetIdx === -1 || token === null) {
            setStatus("error");
            return;
        }
        setStatus("loading");
        let features = await getManyTracksAudioFeatures(token.accessToken, likedSongs.map(s => s.id));
        if (features === ResponseStatus.UnknownError || features === ResponseStatus.Unauthorized) {
            setStatus("error");
            return;
        }
        const targetFeature = features[targetIdx];
        const allSongs: (Track & {features: AudioFeature})[] = likedSongs.map((song, idx) => ({...song, features: features !== ResponseStatus.UnknownError && features !== ResponseStatus.Unauthorized ? features[idx] : emptyFeature}) )
        const similarSongs = getSimilarSongsPercent(allSongs, targetFeature, minSimilarity);
        console.log(similarSongs.length);
        setSimilarSongs(similarSongs);
        setTargetSong(targetSong);
        setStatus("loaded");
    }

    const startGroup = (targetSong: Track, addedSongs: Track[]) => {
        setTargetSong(targetSong);
        setGroupSongs(addedSongs);
    }

    useEffect(() => {
        const accessToken = searchParams.get("accessToken");
        const refreshToken = searchParams.get("refreshToken");
        const expiresIn = searchParams.get("expiresIn");
        const now = searchParams.get("now");

        if (!accessToken || !refreshToken || !expiresIn || !now) {
            setStatus("error");
            return;
        }

        getLikedSongsTotalCount(accessToken).then(count => {
            if (count === ResponseStatus.UnknownError) {
                setStatus("error");
                return;
            }
            setTotalLikedCount(count);
            setToken({ accessToken, refreshToken, expiresIn: parseInt(expiresIn) });
            setStatus("loaded");
        })
    }, [ searchParams ]);

    return (
        <div className="w-screen column-centered py-10">
            { 
                status === "loading" && 
                <div className="flex flex-col items-center mt-20">
                    <div className="loading-ellipsis"><div></div><div></div><div></div><div></div></div>
                    <p className="text-center">Loading...</p>
                </div>
            }
            { 
                status === "error" && 
                <>
                    <p>An unknown error occurred.</p> 
                    <Link to="/" className="my-4">Home</Link>
                </>
            }
            { 
                status === "loaded" && likedSongs.length === 0 && 
                <SongCollector collectSongs={collectSongs} collectAllSongs={collectAllSongs} totalLikedCount={totalLikedCount} /> 
            }
            { 
                status === "loaded" && likedSongs.length > 0 && (
                    targetSong !== null ? (
                        groupSongs.length > 0 ?
                        <PlaylistCreator targetSong={targetSong} songs={groupSongs} /> :
                        <GroupBuilder targetSong={targetSong} songList={similarSongs} back={() => {setSimilarSongs([]); setTargetSong(null);}} startGroup={(songs) => startGroup(targetSong, songs)} />
                    ) :
                    <SongSelector songList={likedSongs} back={() => {setLikedSongs([]);}} findSongs={findSimilarSongs} />
                ) 
            }
        </div>
    );
}

export default SongOrganizer;
